import { buildCartesianAggregationLegendLabelHtml, chartHasComparisonLegendRows, resolveLegendSeriesUserOptions } from '../../../utils/chart-legend-scale.util';
import { resolveCssNumber } from '../../../utils/resolve-highcharts-css-colors.util';
import Highcharts from '../highcharts.config';

const formatNumber = (value: number) => {
	// add commas and make decimal optional, only show if it's not 0
	return value
		.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})
		.replace(/\.00$/, '');
};

/**
 * Default configuration options for bar charts
 */
export const barChartDefaults: Highcharts.Options = {
	title: {
		text: null,
	},
	subtitle: {
		text: null,
	},
	series: [],
	chart: {
		type: 'bar',
		animation: true,
		style: {
			fontFamily:
				'-apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
		},
		spacing: [10, 20, 10, 20],
		backgroundColor: 'var(--background-color)',
	},
	xAxis: {
		type: 'category',
		labels: {
			style: {
				color: 'var(--text-secondary-color)',
			},
		},
		tickmarkPlacement: 'on',
		gridLineWidth: 0,
		gridLineColor: 'var(--border-primary-color)',
	},
	yAxis: {
		title: {
			text: undefined,
		},
		labels: {
			style: {
				color: 'var(--text-secondary-color)',
			},
		},
		gridLineColor: 'var(--border-primary-color)',
		min: 0,
	},
	tooltip: {
		outside: true,
		enabled: true,
		shared: true,
		useHTML: true,
		shadow: {
			color: 'var(--card-shadow-color)',
			opacity: 1,
			offsetX: 0,
			offsetY: 4,
			width: 12,
		},
		backgroundColor: 'var(--background-primary-color)',
		borderColor: 'var(--border-primary-color)',
		borderWidth: resolveCssNumber('--card-border-width', 1),
		borderRadius: resolveCssNumber('--card-border-radius', 6),
		formatter: function (this: any) {
			const points = this.points || [];
			// For bar charts with categories, this.x is the category index (number)
			// We need to look up the actual category name from the xAxis categories
			const xAxis = this.points?.[0]?.series?.xAxis;
			const categories = xAxis?.categories;
			const category =
				categories && typeof this.x === 'number'
					? categories[this.x]
					: this.x;
			let tooltipContent = `<div style="font-size: var(--font-size-xs); font-weight: var(--font-weight-regular); color: var(--text-secondary-color); margin-bottom: 5px;">${category}</div>`;

			// Helper function to get bar style - uses the series color
			const getBarStyle = (point: any) => {
				return `background-color: ${point.series.color}`;
			};

			points.forEach((point: any) => {
				// Build tooltip content
				tooltipContent += `
                <div style="display: flex; align-items: center; margin-bottom: 5px;">
                    <div style="width: 3px; height: 30px; ${getBarStyle(
						point
					)}; margin-right: 8px;"></div>
                    <div style="display: flex; flex-direction: column;">
                        <span style="color: var(--text-secondary-color); font-size: var(--font-size-xs); font-weight: var(--font-weight-regular);">${
							point.series.name
						}</span>
                        <div style="color: var(--text-primary-color); font-weight: var(--font-weight-regular); font-size: var(--font-size-sm)">
                            ${formatNumber(point.y)}
                        </div>
                    </div>
                </div>`;
			});

			return `<div style="padding: 5px; background: var(--background-primary-color);">${tooltipContent}</div>`;
		},
		style: {
			color: 'var(--text-primary-color)',
			fontSize: 'var(--font-size-sm)',
		},
	},
	plotOptions: {
		bar: {
			borderRadius: 3,
			pointPadding: 0.2,
			groupPadding: 0.1,
			borderWidth: 0,
			colorByPoint: false,
			dataLabels: {
				enabled: false,
				// Explicit color — Highcharts `contrast` vs CSS-var chart bg is transparent
				style: {
					color: 'var(--text-primary-color)',
					textOutline: '1px var(--card-background-color, #fff)',
					fontSize: '11px',
				},
			},
			states: {
				hover: {
					brightness: 0.1,
				},
			},
		},
		series: {
			animation: {
				duration: 100,
			},
			opacity: 1,
			borderWidth: 0,
			zIndex: 1,
		},
	},
	legend: {
		enabled: true,
		align: 'left',
		verticalAlign: 'top',
		layout: 'horizontal',
		useHTML: true,
		labelFormatter: function () {
			const seriesRef = this as any;
			const options = resolveLegendSeriesUserOptions(seriesRef);
			const barStyle = `background-color: ${this.color}`;
			const chart = seriesRef.chart;

			return buildCartesianAggregationLegendLabelHtml({
				seriesName: this.name,
				swatchInlineStyle: barStyle,
				aggregationValue: options.aggregationValue,
				comparisonAggregationValue: options.comparisonAggregationValue,
				comparisonData: options.comparisonData,
				formatNumber,
				comparisonRowEnabled: chartHasComparisonLegendRows(chart?.series),
			});
		},
		itemStyle: {
			color: 'var(--text-primary-color)',
			fontWeight: 'normal',
			fontSize: 'var(--font-size-md)',
		},
		itemHoverStyle: {
			color: 'var(--text-primary-color)',
		},
		symbolHeight: 0,
		symbolWidth: 0,
		symbolRadius: 0,
		symbolPadding: 4,
		itemDistance: 12,
		alignColumns: true,
		itemMarginBottom: 2,
		navigation: {
			activeColor: 'var(--text-link-color)',
			inactiveColor: 'var(--text-secondary-color)',
			animation: true,
		},
	},
	credits: {
		enabled: false,
	},
};
